import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { EmptyState } from '@/components/shared/EmptyState';
import { Button } from '@/components/ui/button';
import { Callout } from '@/components/ui/callout';
import { Card } from '@/components/ui/card/Card';
import { Spinner } from '@/components/ui/spinner';
import { useApiClient } from '@/hooks/useApiClient';
import { useTranslation } from '@/hooks/useTranslation';
import { useToolsFetch } from '@/features/toolView/hooks/useToolsFetch';
import { ModelPredictionApiClient } from '../api/ModelPredictionApiClient';
import { WearApiClient } from '../api/WearApiClient';
import { matchesReference, useAdaptcutTools } from '../hooks/useAdaptcutTools';
import { useWearPrediction } from '../hooks/useWearPrediction';
import { formatNumber, formatWear } from '../lib/wearFormat';
import type { FeedbackRating, RecentPrediction } from '../type/ModelPredictionTypes';
import type { WearQuery, WearTrace } from '../type/WearTypes';
import { RecentPredictionsCard } from './RecentPredictionsCard';
import { WearBanners } from './WearBanners';
import { WearCapacityCard } from './WearCapacityCard';
import { WearContextPanel } from './WearContextPanel';
import type { WearSaveStatus } from './WearContextPanel';
import { WearDiameterField } from './WearDiameterField';
import type { DiameterMode } from './WearDiameterField';
import { WearHistoryCard } from './WearHistoryCard';
import { WearNextOperation } from './WearNextOperation';
import type { WearNextField, WearNextValues } from './WearNextOperation';
import { WearStateCard } from './WearStateCard';

/** The user's diameter choice; `null` = automatic (the Adaptcut tool of the same reference). */
type DiameterChoice = { mode: DiameterMode; adaptcutToolId: number | null };

const EMPTY_NEXT: WearNextValues = { fz: '', ae: '', volumePerPiece: '', plannedPieces: '' };

function positive(value: string): number | null {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function positiveInt(value: string): number | null {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
}

/** Next-operation defaults from the newest traces: fz and ae, and the volume per piece. */
function traceDefaults(traces: WearTrace[]): Partial<WearNextValues> {
  const defaults: Partial<WearNextValues> = {};
  for (const trace of [...traces].reverse()) {
    if (defaults.fz === undefined && trace.fz && trace.fz > 0 && trace.ae && trace.ae > 0) {
      defaults.fz = String(trace.fz);
      defaults.ae = String(trace.ae);
    }
    if (
      defaults.volumePerPiece === undefined &&
      trace.volume &&
      trace.volume > 0 &&
      trace.pieces &&
      trace.pieces > 0
    ) {
      defaults.volumePerPiece = String(Math.round((trace.volume / trace.pieces) * 10000) / 10000);
    }
  }
  return defaults;
}

/**
 * /ml "Usure de l'outil": wear_factor computed live from the tool's traceability
 * history; "Enregistrer la prédiction" saves the current result to the list below.
 */
export function MLWearView() {
  const { t, language } = useTranslation();
  const http = useApiClient();
  const wearClient = useMemo(() => (http ? new WearApiClient(http) : null), [http]);
  const predictionClient = useMemo(
    () => (http ? new ModelPredictionApiClient(http) : null),
    [http]
  );
  const { hierarchy, isLoading: toolsLoading, apiResult, handleRetry } = useToolsFetch();
  const adaptcut = useAdaptcutTools(wearClient);

  const [toolId, setToolId] = useState<string | null>(null);
  const [diameterChoice, setDiameterChoice] = useState<DiameterChoice | null>(null);
  const [manualDiameter, setManualDiameter] = useState('');
  const [nextEdits, setNextEdits] = useState<Partial<WearNextValues>>({});
  const [save, setSave] = useState<{ key: string | null; status: WearSaveStatus }>({
    key: null,
    status: 'idle',
  });
  const [recent, setRecent] = useState<RecentPrediction[]>([]);

  const reference = useMemo(
    () => hierarchy.find((ref) => ref.tools.some((tool) => tool.id === toolId))?.reference ?? null,
    [hierarchy, toolId]
  );

  // Diameter: the Adaptcut tool of the same reference unless the user chose otherwise.
  const adaptcutOffered = adaptcut.status !== 'forbidden';
  const sameReferenceTool = useMemo(
    () =>
      adaptcut.tools.find(
        (tool) => matchesReference(tool, reference) && tool.diameterCut !== null
      ) ?? null,
    [adaptcut.tools, reference]
  );
  const choice: DiameterChoice = diameterChoice ?? {
    mode: sameReferenceTool ? 'adaptcut' : 'manual',
    adaptcutToolId: sameReferenceTool?.id ?? null,
  };
  const mode: DiameterMode = adaptcutOffered ? choice.mode : 'manual';
  const adaptcutTool =
    mode === 'adaptcut'
      ? (adaptcut.tools.find((tool) => tool.id === choice.adaptcutToolId) ?? null)
      : null;
  const adaptcutDiameter =
    mode === 'adaptcut' && adaptcut.status === 'ok' ? (adaptcutTool?.diameterCut ?? null) : null;
  const diameter = adaptcutDiameter ?? positive(manualDiameter);

  // First computation without the next operation; its traces give the defaults of step 2.
  const baseQuery = useMemo<WearQuery | null>(
    () => (toolId !== null && diameter !== null ? { toolId, diameter } : null),
    [toolId, diameter]
  );
  const [defaultsTraces, setDefaultsTraces] = useState<{
    toolId: string;
    traces: WearTrace[];
  } | null>(null);
  const defaults = useMemo(
    () =>
      defaultsTraces && defaultsTraces.toolId === toolId
        ? traceDefaults(defaultsTraces.traces)
        : {},
    [defaultsTraces, toolId]
  );
  const nextValues: WearNextValues = { ...EMPTY_NEXT, ...defaults, ...nextEdits };
  const nextEdited = Object.keys(nextEdits).some((key) => key !== 'plannedPieces');
  const hasTraceDefaults = Object.keys(defaults).length > 0;

  const query = useMemo<WearQuery | null>(() => {
    if (!baseQuery) return null;
    const result: WearQuery = { ...baseQuery };
    const fz = positive(nextValues.fz);
    const ae = positive(nextValues.ae);
    if (fz !== null && ae !== null) {
      result.nextFz = fz;
      result.nextAe = ae;
    }
    const volumePerPiece = positive(nextValues.volumePerPiece);
    if (volumePerPiece !== null) result.volumePerPiece = volumePerPiece;
    const plannedPieces = positiveInt(nextValues.plannedPieces);
    if (plannedPieces !== null) result.plannedPieces = plannedPieces;
    return result;
  }, [
    baseQuery,
    nextValues.fz,
    nextValues.ae,
    nextValues.volumePerPiece,
    nextValues.plannedPieces,
  ]);

  const wear = useWearPrediction(wearClient, query);
  const queryKey = query ? JSON.stringify(query) : null;

  // The step-2 defaults are captured from the first result of each tool and kept, so a later
  // failed computation cannot clear them (that would change the query and refetch in a loop).
  if (wear.data && defaultsTraces?.toolId !== wear.data.toolId) {
    setDefaultsTraces({ toolId: wear.data.toolId, traces: wear.data.traces });
  }

  const refreshRecent = useCallback(() => {
    if (!predictionClient) return;
    void predictionClient.recentPredictions('wear').then((res) => {
      if (res.ok) setRecent(res.data.predictions);
    });
  }, [predictionClient]);

  useEffect(() => {
    refreshRecent();
  }, [refreshRecent]);

  const resetTool = useCallback((id: string | null) => {
    setToolId(id);
    setDiameterChoice(null);
    setManualDiameter('');
    setNextEdits({});
  }, []);

  const handleSave = useCallback(async () => {
    if (!wearClient || !query || queryKey === null) return;
    setSave({ key: queryKey, status: 'saving' });
    const res = await wearClient.save({
      ...query,
      diameterSource: adaptcutDiameter !== null ? 'adaptcut' : 'manual',
      adaptcutToolName: adaptcutDiameter !== null ? (adaptcutTool?.name ?? undefined) : undefined,
    });
    setSave({ key: queryKey, status: res.ok ? 'saved' : 'error' });
    if (res.ok) refreshRecent();
  }, [wearClient, query, queryKey, adaptcutDiameter, adaptcutTool, refreshRecent]);

  const handleFeedback = useCallback(
    async (predictionId: string, rating: FeedbackRating) => {
      if (!predictionClient) return;
      const res = await predictionClient.submitFeedback(predictionId, rating);
      if (res.ok) refreshRecent();
    },
    [predictionClient, refreshRecent]
  );

  const saveStatus: WearSaveStatus = save.key === queryKey ? save.status : 'idle';
  const modelUnavailable = wear.error?.code === 503;

  let results: ReactNode;
  if (toolId === null) {
    results = <EmptyState title={t('mlWear.result.idle')} />;
  } else if (diameter === null) {
    results = (
      <EmptyState title={t('mlWear.result.needsDiameter')} data-testid="wear-needs-diameter-card" />
    );
  } else if (modelUnavailable) {
    results = (
      <Callout
        tone="error"
        title={t('mlWear.result.modelUnavailableTitle')}
        data-testid="wear-model-unavailable"
      >
        {t('mlWear.result.modelUnavailable')}
      </Callout>
    );
  } else if (wear.error) {
    results = (
      <Callout
        tone="error"
        title={t('mlWear.result.error')}
        data-testid="wear-error"
        action={
          <Button variant="outline" size="sm" className="w-fit" onClick={wear.retry}>
            {t('mlWear.context.retry')}
          </Button>
        }
      >
        {wear.error.message || t('mlWear.result.error')}
      </Callout>
    );
  } else if (!wear.data || !query) {
    results = (
      <Card
        as="section"
        padding="none"
        className="flex items-center justify-center gap-2 p-10 text-sm text-muted-foreground"
        data-testid="wear-loading"
      >
        <Spinner />
        {t('mlWear.result.loading')}
      </Card>
    );
  } else {
    results = (
      <>
        <WearBanners output={wear.data} />
        <WearStateCard output={wear.data} />
        <WearCapacityCard output={wear.data} query={query} />
        <WearHistoryCard output={wear.data} />
      </>
    );
  }

  const recentColumns = [
    {
      key: 'left',
      header: t('mlWear.recent.left'),
      numeric: true,
      render: (prediction: RecentPrediction) => {
        const pieces = prediction.outputs.pieces_left_p50?.[0];
        const volume = prediction.outputs.volume_left_p50_cm3?.[0];
        if (volume === undefined) return '—';
        const shownVolume = formatNumber(language, volume, 2);
        return pieces !== undefined
          ? t('mlWear.recent.leftPieces', { pieces, volume: shownVolume })
          : t('mlWear.recent.leftVolume', { volume: shownVolume });
      },
    },
    {
      key: 'settings',
      header: t('mlWear.recent.settings'),
      render: (prediction: RecentPrediction) => String(prediction.context?.settings ?? '—'),
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
        <div className="w-full lg:sticky lg:top-6 lg:w-[360px] lg:shrink-0">
          <WearContextPanel
            references={hierarchy}
            toolsLoading={toolsLoading}
            toolsError={!toolsLoading && apiResult === null && hierarchy.length === 0}
            onToolsRetry={handleRetry}
            toolId={toolId}
            onToolChange={(id) => resetTool(id)}
            onReset={() => resetTool(null)}
            diameterField={
              <WearDiameterField
                adaptcutStatus={adaptcut.status}
                adaptcutTools={adaptcut.tools}
                onAdaptcutRetry={adaptcut.retry}
                reference={reference}
                mode={mode}
                onModeChange={(next) =>
                  setDiameterChoice({
                    mode: next,
                    adaptcutToolId:
                      next === 'adaptcut'
                        ? (choice.adaptcutToolId ?? sameReferenceTool?.id ?? null)
                        : null,
                  })
                }
                adaptcutTool={adaptcutTool}
                onAdaptcutToolChange={(tool) =>
                  setDiameterChoice({ mode: 'adaptcut', adaptcutToolId: tool.id })
                }
                manualValue={manualDiameter}
                onManualChange={setManualDiameter}
                disabled={false}
              />
            }
            nextOperation={
              <WearNextOperation
                values={nextValues}
                onChange={(field: WearNextField, value: string) =>
                  setNextEdits((edits) => ({ ...edits, [field]: value }))
                }
                hasTraceDefaults={hasTraceDefaults}
                edited={nextEdited}
                onBackToTrace={() =>
                  setNextEdits((edits) =>
                    edits.plannedPieces !== undefined ? { plannedPieces: edits.plannedPieces } : {}
                  )
                }
                disabled={false}
              />
            }
            canSave={Boolean(query && wear.data && !wear.isLoading && !wear.error)}
            saveStatus={saveStatus}
            onSave={() => void handleSave()}
            needsDiameter={diameter === null}
          />
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-4" aria-live="polite">
          {results}
        </div>
      </div>
      <RecentPredictionsCard
        predictions={recent}
        onFeedback={handleFeedback}
        formatValue={(prediction) => {
          const value = prediction.outputs.wear?.[0];
          return value === undefined ? '—' : formatWear(language, value);
        }}
        valueHeader={t('mlWear.result.wear')}
        extraColumns={recentColumns}
      />
    </div>
  );
}
